import { useState, useEffect } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7bhrit8u96f4i6v675g';
const STORAGE_KEY = 'site_reviews_v1';

interface Review {
  id: string;
  name: string;
  city: string;
  rating: number;
  text: string;
  date: string;
}

function loadReviews(): Review[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Review[]) : [];
  } catch {
    return [];
  }
}

function saveReviews(reviews: Review[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reviews));
  } catch {
    // ignore
  }
}

export default function TestimonialsSection() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [form, setForm] = useState({ name: '', city: '', rating: 5, text: '' });
  const [hoveredStar, setHoveredStar] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Load saved reviews on mount
  useEffect(() => {
    setReviews(loadReviews());
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      setError('يرجى كتابة اسمك ورأيك على الأقل');
      return;
    }
    if (form.text.length > 500) {
      setError('الرأي يجب أن لا يتجاوز 500 حرف');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: form.name,
        city: form.city,
        rating: String(form.rating),
        text: form.text,
      });
      await fetch(FORM_URL, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const newReview: Review = {
        id: Date.now().toString(),
        name: form.name,
        city: form.city,
        rating: form.rating,
        text: form.text,
        date: new Date().toLocaleDateString('ar-AE', { year: 'numeric', month: 'long', day: 'numeric' }),
      };

      const updated = [newReview, ...reviews];
      setReviews(updated);
      saveReviews(updated);
      setSubmitted(true);
      setShowForm(false);
      setForm({ name: '', city: '', rating: 5, text: '' });
    } catch {
      setError('حدث خطأ، يرجى المحاولة مجدداً');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-24 px-6 lg:px-12 bg-gray-950 overflow-hidden relative" dir="rtl">
      {/* Decorative bg */}
      <div className="absolute inset-0 opacity-5 pointer-events-none select-none">
        <div className="absolute top-10 right-20 text-[200px] font-black text-[#C9A961] leading-none">&ldquo;</div>
        <div className="absolute bottom-10 left-20 text-[200px] font-black text-[#C9A961] leading-none rotate-180">&ldquo;</div>
      </div>

      <div className="max-w-4xl mx-auto relative z-10">

        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-[#C9A961] text-sm font-bold tracking-widest uppercase mb-3 block">آراء عملائنا</span>
          <h2 className="text-4xl font-black text-white mb-3">ماذا يقولون عنا</h2>
          <p className="text-white/40 text-sm">تقييمات حقيقية من عملاء حقيقيين — لا كذب ولا تزوير</p>
        </div>

        {/* Empty state */}
        {reviews.length === 0 && !submitted && (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/5 mb-5">
              <i className="ri-chat-smile-3-line text-4xl text-[#C9A961]/60" />
            </div>
            <p className="text-white/50 text-base font-semibold mb-1">لا توجد تقييمات بعد</p>
            <p className="text-white/30 text-sm mb-6">كن أول من يشارك رأيه الحقيقي!</p>
          </div>
        )}

        {/* Reviews grid */}
        {reviews.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-10">
            {reviews.map(review => (
              <div
                key={review.id}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 text-right"
              >
                {/* Stars */}
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-star-fill text-sm ${i < review.rating ? 'text-[#C9A961]' : 'text-white/20'}`} />
                    </div>
                  ))}
                  <span className="text-white/30 text-[10px] mr-1">
                    {['', 'سيء', 'مقبول', 'جيد', 'ممتاز', 'رائع جداً'][review.rating]}
                  </span>
                </div>

                {/* Text */}
                <p className="text-white/70 text-sm leading-relaxed mb-5">&laquo;{review.text}&raquo;</p>

                {/* Author */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C9A961]/20 flex-shrink-0">
                      <i className="ri-user-line text-[#C9A961] text-sm" />
                    </div>
                    <div>
                      <p className="text-white/80 text-xs font-bold">{review.name}</p>
                      {review.city && <p className="text-white/30 text-[10px]">{review.city}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-verified-badge-fill text-[#C9A961] text-xs" />
                    </div>
                    <span className="text-[#C9A961] text-[10px] font-bold">عميل حقيقي</span>
                  </div>
                </div>
                <p className="text-white/20 text-[10px] mt-2 text-left">{review.date}</p>
              </div>
            ))}
          </div>
        )}

        {/* Success message */}
        {submitted && !showForm && (
          <div className="flex items-center justify-center gap-3 bg-green-900/30 border border-green-500/30 rounded-2xl px-6 py-4 mb-8">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-green-400 text-xl" />
            </div>
            <p className="text-green-400 text-sm font-bold">شكراً! تم نشر رأيك بنجاح ✓</p>
          </div>
        )}

        {/* Add review button */}
        {!showForm && (
          <div className="flex justify-center">
            <button
              onClick={() => { setShowForm(true); setSubmitted(false); }}
              className="flex items-center gap-2.5 bg-[#C9A961] hover:bg-[#A07C30] text-white px-8 py-3.5 rounded-full font-bold text-sm cursor-pointer transition-all duration-300 whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-edit-2-line" />
              </div>
              أضف رأيك الحقيقي
            </button>
          </div>
        )}

        {/* Review form */}
        {showForm && (
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="bg-white/5 border border-white/10 rounded-3xl p-8 mt-4"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-black text-lg">شارك رأيك الحقيقي</h3>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/60 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-base" />
              </button>
            </div>

            {/* Stars */}
            <div className="mb-5">
              <label className="text-white/60 text-xs font-semibold mb-2 block">تقييمك من 5 نجوم</label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, rating: star }))}
                    onMouseEnter={() => setHoveredStar(star)}
                    onMouseLeave={() => setHoveredStar(0)}
                    className="w-9 h-9 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                  >
                    <i className={`ri-star-fill text-2xl transition-colors ${
                      star <= (hoveredStar || form.rating) ? 'text-[#C9A961]' : 'text-white/20'
                    }`} />
                  </button>
                ))}
                <span className="text-white/50 text-xs mr-1 font-semibold">
                  {['', 'سيء', 'مقبول', 'جيد', 'ممتاز', 'رائع جداً'][hoveredStar || form.rating]}
                </span>
              </div>
            </div>

            {/* Name + City */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-white/60 text-xs font-semibold mb-1.5 block">
                  اسمك <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="مثال: محمد أحمد"
                  className="w-full bg-white/10 border border-white/10 focus:border-[#C9A961]/50 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none transition-colors"
                />
              </div>
              <div>
                <label className="text-white/60 text-xs font-semibold mb-1.5 block">مدينتك (اختياري)</label>
                <input
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                  placeholder="مثال: دبي، الإمارات"
                  className="w-full bg-white/10 border border-white/10 focus:border-[#C9A961]/50 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none transition-colors"
                />
              </div>
            </div>

            {/* Review text */}
            <div className="mb-5">
              <label className="text-white/60 text-xs font-semibold mb-1.5 block">
                رأيك بصدق <span className="text-red-400">*</span>
              </label>
              <textarea
                name="text"
                value={form.text}
                onChange={e => setForm(f => ({ ...f, text: e.target.value.slice(0, 500) }))}
                rows={4}
                placeholder="اكتب تجربتك الحقيقية مع المنتج أو الخدمة..."
                className="w-full bg-white/10 border border-white/10 focus:border-[#C9A961]/50 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none transition-colors resize-none"
              />
              <p className="text-white/20 text-[10px] text-left mt-1">{form.text.length}/500</p>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 bg-red-900/30 border border-red-500/30 rounded-xl px-4 py-3 mb-4">
                <i className="ri-error-warning-line text-red-400 text-sm" />
                <p className="text-red-400 text-xs">{error}</p>
              </div>
            )}

            {/* Honesty note */}
            <div className="flex items-start gap-2 bg-[#C9A961]/10 rounded-xl px-4 py-3 mb-5">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-[#C9A961] text-sm" />
              </div>
              <p className="text-[#C9A961]/80 text-xs leading-relaxed">
                نحن نؤمن بالصدق التام — رأيك الحقيقي سواء كان إيجابياً أو سلبياً يساعدنا على التحسين وبناء الثقة مع عملائنا.
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#C9A961] hover:bg-[#A07C30] disabled:opacity-50 text-white py-3.5 rounded-xl font-bold text-sm cursor-pointer transition-all duration-300 whitespace-nowrap"
            >
              {submitting ? 'جارٍ الإرسال...' : 'إرسال رأيي الحقيقي'}
            </button>
          </form>
        )}

      </div>
    </section>
  );
}
